import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { apiClient, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Phone,
  Mail,
  FileText,
  Calendar,
  Edit,
  Trash2,
  X,
  Building,
  MapPin,
  Globe,
  Linkedin,
  Loader2,
  Plus,
  ExternalLink,
  Paperclip,
  Download,
  Check,
  Upload,
} from "lucide-react";

const eventIcons = {
  call: { icon: Phone, class: "event-icon-call" },
  email: { icon: Mail, class: "event-icon-email" },
  note: { icon: FileText, class: "event-icon-note" },
  meeting: { icon: Calendar, class: "event-icon-meeting" },
  other: { icon: FileText, class: "event-icon-other" },
};

const eventTypeLabels = {
  call: "Anruf",
  email: "E-Mail",
  note: "Notiz",
  meeting: "Meeting",
  other: "Sonstiges",
};

const getTagStyle = (color) => ({
  backgroundColor: `${color}20`,
  color: color,
  borderColor: `${color}40`,
});

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeUrl = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const normalizeLinkedin = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const clean = value.replace(/^linkedin\.com\//i, "").replace(/^\/+/, "");
  if (clean.includes("/")) return `https://www.linkedin.com/${clean}`;
  return `https://www.linkedin.com/in/${clean}`;
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit customer modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState({});

  // Event modal (add or edit)
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventData, setEventData] = useState({ type: "call", description: "", date: "" });

  // Upload state
  const [uploadingEventId, setUploadingEventId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const token = localStorage.getItem("token");

  const fetchData = async () => {
    try {
      const [customerRes, tagsRes] = await Promise.all([
        apiClient.get(`/customers/${id}`),
        apiClient.get("/tags"),
      ]);
      setCustomer(customerRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Details");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const refreshCustomer = async () => {
    const res = await apiClient.get(`/customers/${id}`);
    setCustomer(res.data);
  };

  // ---- Customer edit ----
  const openEditModal = () => {
    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      company: customer.company || "",
      homepage: customer.homepage || "",
      linkedin: customer.linkedin || "",
      address: customer.address || "",
      notes: customer.notes || "",
      tag_ids: customer.tag_ids || [],
    });
    setShowEditModal(true);
  };

  const handleSaveCustomer = async () => {
    if (!formData.name?.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      await apiClient.put(`/customers/${id}`, formData);
      toast.success("Kunde aktualisiert");
      setShowEditModal(false);
      refreshCustomer();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!window.confirm("Diesen Kunden in den Papierkorb verschieben?")) return;
    try {
      await apiClient.delete(`/customers/${id}`);
      toast.success("In den Papierkorb verschoben");
      navigate(-1);
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // ---- Events ----
  const openAddEvent = () => {
    setEditingEvent(null);
    setEventData({ type: "call", description: "", date: new Date().toISOString().split("T")[0] });
    setShowEventModal(true);
  };

  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventData({
      type: event.type,
      description: event.description,
      date: event.date ? new Date(event.date).toISOString().split("T")[0] : new Date().toISOString().split("T")[0],
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async () => {
    if (!eventData.description.trim()) {
      toast.error("Beschreibung ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      if (editingEvent) {
        await apiClient.put(`/customers/${id}/events/${editingEvent.id}`, eventData);
        toast.success("Ereignis aktualisiert");
      } else {
        await apiClient.post(`/customers/${id}/events`, eventData);
        toast.success("Ereignis hinzugefügt");
      }
      setShowEventModal(false);
      refreshCustomer();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Dieses Ereignis inkl. aller Dateien löschen?")) return;
    try {
      await apiClient.delete(`/customers/${id}/events/${eventId}`);
      toast.success("Ereignis gelöscht");
      refreshCustomer();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // ---- Attachments ----
  const triggerUpload = (eventId) => {
    uploadTargetRef.current = eventId;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    const eventId = uploadTargetRef.current;
    if (!files.length || !eventId) return;

    const MAX = 50 * 1024 * 1024;
    const tooBig = files.find((f) => f.size > MAX);
    if (tooBig) {
      toast.error(`Datei '${tooBig.name}' ist zu groß (max. 50 MB)`);
      return;
    }

    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));

    setUploadingEventId(eventId);
    try {
      await apiClient.post(`/customers/${id}/events/${eventId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${files.length} Datei(en) hochgeladen`);
      refreshCustomer();
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    } finally {
      setUploadingEventId(null);
      uploadTargetRef.current = null;
    }
  };

  const handleDeleteAttachment = async (eventId, attId) => {
    if (!window.confirm("Diese Datei löschen?")) return;
    try {
      await apiClient.delete(`/customers/${id}/events/${eventId}/attachments/${attId}`);
      toast.success("Datei gelöscht");
      refreshCustomer();
    } catch (error) {
      toast.error("Fehler beim Löschen der Datei");
    }
  };

  const attachmentUrl = (eventId, attId) =>
    `${API}/customers/${id}/events/${eventId}/attachments/${attId}?auth=${token}`;

  if (loading) {
    return (
      <Layout title="Kundendetails">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  if (!customer) return null;

  const infoItems = [
    customer.email && { icon: Mail, label: "E-Mail", value: customer.email, href: `mailto:${customer.email}` },
    customer.phone && { icon: Phone, label: "Telefon", value: customer.phone, href: `tel:${customer.phone}` },
    customer.company && { icon: Building, label: "Firma", value: customer.company },
    customer.homepage && { icon: Globe, label: "Homepage", value: customer.homepage, href: normalizeUrl(customer.homepage), external: true },
    customer.linkedin && { icon: Linkedin, label: "LinkedIn", value: customer.linkedin, href: normalizeLinkedin(customer.linkedin), external: true },
    customer.address && { icon: MapPin, label: "Adresse", value: customer.address },
  ].filter(Boolean);

  return (
    <Layout title="Kundendetails">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
        data-testid="hidden-file-input"
      />

      {/* Header with back arrow */}
      <div className="flex items-center justify-between mb-6" data-testid="detail-header">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
            data-testid="detail-back-btn"
            title="Zurück"
          >
            <ArrowLeft className="w-5 h-5 text-slate-700" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate">{customer.name}</h1>
            <p className="text-sm text-slate-500">Erstellt am {formatDateTime(customer.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" onClick={openEditModal} data-testid="detail-edit-btn">
            <Edit className="w-4 h-4 mr-2" />
            Bearbeiten
          </Button>
          <Button variant="outline" onClick={handleDeleteCustomer} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="detail-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Löschen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: info */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="detail-info-card">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wide">Kontaktdaten</h3>
            <div className="space-y-3">
              {infoItems.map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <item.icon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.external ? "_blank" : undefined}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        className="text-sm font-medium text-indigo-600 hover:underline break-all"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm font-medium text-slate-900 break-words">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
              {infoItems.length === 0 && <p className="text-sm text-slate-400">Keine Kontaktdaten</p>}
            </div>
          </div>

          {customer.tags?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Tags</h3>
              <div className="flex flex-wrap gap-2" data-testid="detail-tags">
                {customer.tags.map((tag) => (
                  <Badge key={tag.id} variant="outline" style={getTagStyle(tag.color)}>{tag.name}</Badge>
                ))}
              </div>
            </div>
          )}

          {customer.external_objects?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Verknüpfte Objekte</h3>
              <div className="space-y-2">
                {customer.external_objects.map((obj, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <span className="text-sm font-medium text-slate-900 truncate mr-2">{obj.title}</span>
                    <a href={obj.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0">
                      <ExternalLink className="w-4 h-4" />Öffnen
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {customer.notes && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Notizen</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap break-words">{customer.notes}</p>
            </div>
          )}
        </div>

        {/* Right column: events */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="detail-events-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Ereignisse ({customer.events?.length || 0})
              </h3>
              <Button size="sm" onClick={openAddEvent} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-event-btn">
                <Plus className="w-4 h-4 mr-1" />Ereignis
              </Button>
            </div>

            {customer.events?.length > 0 ? (
              <div className="space-y-3">
                {[...customer.events]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((event) => {
                    const EventIcon = eventIcons[event.type]?.icon || FileText;
                    const iconClass = eventIcons[event.type]?.class || "event-icon-other";
                    return (
                      <div key={event.id} className="p-4 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors" data-testid={`event-item-${event.id}`}>
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${iconClass}`}>
                            <EventIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-slate-900">{eventTypeLabels[event.type] || event.type}</span>
                              <span className="text-xs text-slate-400">{formatDate(event.date)}</span>
                            </div>
                            <p className="text-sm text-slate-600 break-words whitespace-pre-wrap">{event.description}</p>
                            <p className="text-xs text-slate-400 mt-1">von {event.created_by}</p>

                            {/* Attachments */}
                            {event.attachments?.length > 0 && (
                              <div className="mt-3 space-y-1.5">
                                {event.attachments.map((att) => (
                                  <div key={att.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg group" data-testid={`attachment-${att.id}`}>
                                    <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                    <a
                                      href={attachmentUrl(event.id, att.id)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-indigo-600 hover:underline truncate flex-1"
                                      title={att.filename}
                                    >
                                      {att.filename}
                                    </a>
                                    <span className="text-xs text-slate-400 flex-shrink-0">{formatBytes(att.size)}</span>
                                    <a
                                      href={attachmentUrl(event.id, att.id)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-1 hover:bg-slate-200 rounded text-slate-400 hover:text-indigo-600 flex-shrink-0"
                                      title="Herunterladen"
                                    >
                                      <Download className="w-4 h-4" />
                                    </a>
                                    <button
                                      onClick={() => handleDeleteAttachment(event.id, att.id)}
                                      className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 flex-shrink-0"
                                      title="Datei löschen"
                                      data-testid={`delete-attachment-${att.id}`}
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="flex items-center gap-2 mt-3">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() => triggerUpload(event.id)}
                                disabled={uploadingEventId === event.id}
                                data-testid={`upload-file-btn-${event.id}`}
                              >
                                {uploadingEventId === event.id ? (
                                  <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />Lädt...</>
                                ) : (
                                  <><Upload className="w-3.5 h-3.5 mr-1" />Datei hinzufügen</>
                                )}
                              </Button>
                            </div>
                          </div>

                          <div className="flex flex-col gap-1 flex-shrink-0">
                            <button
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-indigo-600 transition-colors"
                              onClick={() => openEditEvent(event)}
                              title="Ereignis bearbeiten"
                              data-testid={`edit-event-btn-${event.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-red-500 transition-colors"
                              onClick={() => handleDeleteEvent(event.id)}
                              title="Ereignis löschen"
                              data-testid={`delete-event-btn-${event.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-12 bg-slate-50 rounded-lg">
                <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">Keine Ereignisse vorhanden</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Customer Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="detail-customer-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Kunde bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name || ""} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input type="email" value={formData.email || ""} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={formData.phone || ""} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Firma</Label>
              <Input value={formData.company || ""} onChange={(e) => setFormData({ ...formData, company: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Homepage</Label>
                <Input value={formData.homepage || ""} onChange={(e) => setFormData({ ...formData, homepage: e.target.value })} placeholder="www.beispiel.de" />
              </div>
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input value={formData.linkedin || ""} onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })} placeholder="Name oder Link" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={formData.address || ""} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 min-h-[60px]">
                {tags.map((tag) => {
                  const isSelected = (formData.tag_ids || []).includes(tag.id);
                  return (
                    <Badge
                      key={tag.id}
                      variant="outline"
                      className={`cursor-pointer transition-all flex items-center gap-1.5 pr-2.5 ${isSelected ? "ring-2 ring-offset-1 ring-indigo-500" : "opacity-60 hover:opacity-80"}`}
                      style={getTagStyle(tag.color)}
                      onClick={() => {
                        const cur = formData.tag_ids || [];
                        const newTags = isSelected ? cur.filter((t) => t !== tag.id) : [...cur, tag.id];
                        setFormData({ ...formData, tag_ids: newTags });
                      }}
                    >
                      {isSelected && <Check className="w-3.5 h-3.5" style={{ color: tag.color }} />}
                      {tag.name}
                    </Badge>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notizen</Label>
              <Textarea value={formData.notes || ""} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Abbrechen</Button>
            <Button onClick={handleSaveCustomer} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="detail-save-customer-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : "Aktualisieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Modal (add/edit) */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="max-w-md" data-testid="event-modal">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Ereignis bearbeiten" : "Ereignis hinzufügen"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Typ</Label>
              <Select value={eventData.type} onValueChange={(value) => setEventData({ ...eventData, type: value })}>
                <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call"><div className="flex items-center gap-2"><Phone className="w-4 h-4" /> Anruf</div></SelectItem>
                  <SelectItem value="email"><div className="flex items-center gap-2"><Mail className="w-4 h-4" /> E-Mail</div></SelectItem>
                  <SelectItem value="note"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Notiz</div></SelectItem>
                  <SelectItem value="meeting"><div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Meeting</div></SelectItem>
                  <SelectItem value="other"><div className="flex items-center gap-2"><FileText className="w-4 h-4" /> Sonstiges</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Datum</Label>
              <Input type="date" value={eventData.date} onChange={(e) => setEventData({ ...eventData, date: e.target.value })} data-testid="event-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung *</Label>
              <Textarea value={eventData.description} onChange={(e) => setEventData({ ...eventData, description: e.target.value })} placeholder="Was wurde besprochen?" rows={3} data-testid="event-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEvent} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-event-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : editingEvent ? "Aktualisieren" : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
